import React, { useState, useEffect } from "react";
import { grabHistoricalPrices } from "../services/metals-services";
import ReactApexChart from "react-apexcharts";
import {
  Typography,
  Stack,
  Divider,
  Button,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import MetalSymbol from "./MetalSymbol";

const MetalPriceChart = ({ metalSymbols, showChart = true }) => {
  const [metalsData, setMetalsData] = useState([]);
  const [fetchDateTime, setFetchDateTime] = useState(new Date());
  const [selectedMetals, setSelectedMetals] = useState(new Set(metalSymbols));
  const [series, setSeries] = useState([]);
  const [selectedMetal, setSelectedMetal] = useState("PT");
  const [chartType, setChartType] = useState("line");
  const [priceType, setPriceType] = useState("bid");

  const toggleMetal = (symbol) => {
    setSelectedMetals((prevSelected) => {
      const newSelected = new Set();
      if (newSelected.has(symbol)) {
        newSelected.delete(symbol);
      } else {
        newSelected.add(symbol);
      }
      return newSelected;
    });
  };

  const toggleChartType = () => {
    setChartType(chartType === "candlestick" ? "line" : "candlestick");
  };

  const handlePriceTypeChange = (event, newPriceType) => {
    if (newPriceType !== null) {
      setPriceType(newPriceType);
    }
  };

  const metalNames = {
    PD: "palladium",
    PT: "platinum",
    RH: "rhodium",
  };

  const metalColors = {
    PD: "#FFD700",
    PT: "#E5E4E2",
    RH: "#24e4ff",
  };

  useEffect(() => {
    const fetchSeriesData = async () => {
      try {
        const metalData = await grabHistoricalPrices(120);
        console.log("Raw metalData:", metalData);

        setFetchDateTime(new Date());

        const metalWord = metalNames[selectedMetal];

        const fiveDaysAgo = new Date();
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

        const filteredData = metalData.filter((t) => {
          if (!t[metalWord]) return false;
          const date = new Date(t[metalWord].date);
          return date >= fiveDaysAgo;
        });

        console.log(
          `Filtered data points for ${selectedMetal} (${metalWord}):`,
          filteredData.length,
        );

        if (chartType === "candlestick") {
          const candleData = filteredData
            .map((t) => ({
              x: new Date(t[metalWord].date).getTime(),
              y: [
                t[metalWord].open || t[metalWord].price,
                t[metalWord].high || t[metalWord].price,
                t[metalWord].low || t[metalWord].price,
                t[metalWord].close || t[metalWord].price,
              ],
            }))
            .sort((a, b) => a.x - b.x);

          console.log("Candlestick data:", candleData);

          setSeries([
            {
              name: selectedMetal,
              data: candleData,
            },
          ]);
        } else {
          const lineData = filteredData
            .map((t) => ({
              x: new Date(t[metalWord].date).getTime(),
              y:
                priceType === "bid"
                  ? t[metalWord].bid || t[metalWord].price
                  : t[metalWord].ask || t[metalWord].price,
            }))
            .sort((a, b) => a.x - b.x);

          setSeries([
            {
              name: `${selectedMetal} (${priceType.toUpperCase()})`,
              data: lineData,
            },
          ]);
        }

        const allMetalsData = [];

        for (const m of ["PD", "PT", "RH"]) {
          const metalWord = metalNames[m];

          const filteredData = metalData.filter((t) => {
            if (!t[metalWord]) return false;
            const date = new Date(t[metalWord].date);
            return date >= fiveDaysAgo;
          });

          if (filteredData.length > 0) {
            const latestData = filteredData.sort(
              (a, b) =>
                new Date(b[metalWord].date).getTime() -
                new Date(a[metalWord].date).getTime(),
            )[0];

            const latestPrice =
              priceType === "bid"
                ? latestData[metalWord].bid || latestData[metalWord].price
                : latestData[metalWord].ask || latestData[metalWord].price;

            const newData = {
              name: m,
              data: filteredData
                .map((t) => ({
                  x: new Date(t[metalWord].date).getTime(),
                  y:
                    priceType === "bid"
                      ? t[metalWord].bid || t[metalWord].price
                      : t[metalWord].ask || t[metalWord].price,
                }))
                .sort((a, b) => a.x - b.x),
              latestPrice: latestPrice,
            };

            allMetalsData.push(newData);
          }
        }

        setMetalsData(allMetalsData);
        console.log("All metals data set:", allMetalsData);
      } catch (error) {
        console.error("Error fetching series data:", error);
        if (error.errors) {
          console.error("GraphQL errors:", error.errors);
        }
      }
    };

    fetchSeriesData();
  }, [selectedMetal, chartType, priceType]);

  const options = {
    chart: {
      type: chartType,
      height: 350,
      background: "#000000",
      zoom: {
        enabled: true,
        type: "x",
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      foreColor: "#fff",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    title: {
      text: `${selectedMetal} ${priceType.toUpperCase()} Price`,
      align: "left",
      style: {
        fontSize: window.innerWidth < 600 ? "12px" : "14px",
        fontWeight: "bold",
        color: "#fff",
      },
    },
    grid: {
      borderColor: "#333333",
      xaxis: {
        lines: {
          show: true,
          opacity: 0.15,
        },
      },
      yaxis: {
        lines: {
          show: true,
          opacity: 0.15,
        },
      },
      padding: {
        top: 0,
        right: 10,
        bottom: 0,
        left: 10,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: chartType === "line" ? "smooth" : "straight",
      width: chartType === "line" ? 6 : 2,
      lineCap: "round",
    },
    markers:
      chartType === "line"
        ? {
            size: 6,
            strokeWidth: 2,
            strokeColors: ["#fff"],
            colors: [metalColors[selectedMetal] || "#24e4ff"],
            hover: {
              size: 8,
            },
          }
        : {
            size: 0,
          },
    xaxis: {
      type: "datetime",
      labels: {
        style: {
          colors: "#fff",
          fontSize: window.innerWidth < 600 ? "10px" : "12px",
        },
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM 'yy",
          day: "dd MMM",
          hour: "HH:mm",
        },
        formatter: function (value, timestamp) {
          const date = new Date(timestamp);
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        },
        rotate: window.innerWidth < 600 ? -45 : 0,
      },
      axisBorder: {
        color: "#333333",
      },
      axisTicks: {
        color: "#333333",
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#fff",
          fontSize: window.innerWidth < 600 ? "10px" : "12px",
        },
        formatter: (value) =>
          window.innerWidth < 600
            ? `$${value.toFixed(0)}`
            : `$${value.toFixed(2)}`,
      },
      tooltip: {
        enabled: true,
      },
      title: {
        text: `${priceType.toUpperCase()} Price (USD)`,
        style: {
          color: "#fff",
          fontSize: window.innerWidth < 600 ? "10px" : "12px",
        },
      },
    },
    tooltip: {
      x: {
        format: "dd MMM yyyy HH:mm",
      },
      theme: "dark",
      style: {
        fontSize: "12px",
        fontFamily: "Poppins",
      },
      marker: {
        show: true,
      },
      y: {
        formatter: function (value) {
          if (Array.isArray(value)) {
            return `
              Open: $${value[0].toFixed(2)}
              High: $${value[1].toFixed(2)}
              Low: $${value[2].toFixed(2)}
              Close: $${value[3].toFixed(2)}
            `;
          }
          return `${priceType.toUpperCase()}: $${value.toFixed(2)}`;
        },
      },
    },
    plotOptions: {
      candlestick: {
        colors: {
          upward: "#26a69a",
          downward: "#ef5350",
        },
        wick: {
          useFillColor: true,
        },
      },
    },
    fill: {
      type: "solid",
      opacity: chartType === "line" ? 0.1 : 0.7,
    },
    colors: [metalColors[selectedMetal] || "#24e4ff"],
    legend: {
      show: false,
    },
  };

  return (
    <Stack
      sx={{
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "background.default",
        p: { xs: 1, sm: 2, md: 3 },
        borderRadius: 2,
        border: 1,
        borderColor: "primary.main",
        width: "100%",
      }}
      spacing={1}
    >
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 1, md: 1 }}
        style={{ margin: "10px" }}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          mb: { xs: 1, sm: 2 },
        }}
      >
        {metalsData.map((metal, i) => (
          <div
            onClick={() => setSelectedMetal(metal.name)}
            style={{
              cursor: "pointer",
              opacity: selectedMetal === metal.name ? 1 : 0.7,
            }}
            key={i}
          >
            <MetalSymbol symbol={metal.name} price={metal.latestPrice} />
          </div>
        ))}
      </Stack>
      {showChart === true && (
        <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1, md: 0 }}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                color: "text.primary",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              PGM Market Overview - Past 5 Days
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <ToggleButtonGroup
                value={priceType}
                exclusive
                onChange={handlePriceTypeChange}
                size="small"
                sx={{
                  mr: { xs: 0, sm: 1 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <ToggleButton
                  value="bid"
                  sx={{
                    color:
                      priceType === "bid" ? "primary.main" : "text.secondary",
                    borderColor: "primary.main",
                    flex: { xs: 1, sm: "none" },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(64, 243, 72, 0.1)",
                      color: "primary.main",
                    },
                  }}
                >
                  BID
                </ToggleButton>
                <ToggleButton
                  value="ask"
                  sx={{
                    color:
                      priceType === "ask" ? "primary.main" : "text.secondary",
                    borderColor: "primary.main",
                    flex: { xs: 1, sm: "none" },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(64, 243, 72, 0.1)",
                      color: "primary.main",
                    },
                  }}
                >
                  ASK
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                size="small"
                onClick={toggleChartType}
                sx={{
                  color: "primary.main",
                  borderColor: "primary.main",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {chartType === "candlestick" ? "Line Chart" : "Candlestick"}
              </Button>
            </Stack>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              color: "text.secondary",
              alignSelf: "flex-start",
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            Current as of: {fetchDateTime.toLocaleString()} • Showing{" "}
            {priceType.toUpperCase()} prices
          </Typography>

          <Box
            sx={{
              width: "100%",
              backgroundColor: "background.default",
              borderRadius: 1,
              p: { xs: 1, sm: 2 },
              overflow: "hidden",
            }}
          >
            <ReactApexChart
              key={`${chartType}-${selectedMetal}-${priceType}`}
              options={{
                ...options,
                chart: {
                  ...options.chart,
                  height: { xs: 250, sm: 300, md: 350 },
                },
              }}
              series={series}
              type={chartType}
              height={
                window.innerWidth < 600
                  ? 250
                  : window.innerWidth < 900
                    ? 300
                    : 350
              }
              width="100%"
            />
          </Box>

          <Divider
            variant="middle"
            sx={{
              mt: { xs: 1, sm: 2 },
              width: "100%",
              borderColor: "primary.main",
            }}
          />
        </>
      )}
    </Stack>
  );
};

export default MetalPriceChart;
